/**
 * Pure calculation functions for the Client Quote "tira" (strip-cutting
 * optimization) price calculation. No React/DOM dependency — independently
 * unit tested. See docs/frontend/client_quote.md for the formula rationale.
 */

/**
 * Displayed piece area (CM²). Includes cut_thickness on both cutting edges
 * of the piece's width — the kerf allowance lost to each cut.
 */
export const calculateCm2 = (width, height, cutThickness) => {
  const w = parseFloat(width) || 0;
  const h = parseFloat(height) || 0;
  const t = parseFloat(cutThickness) || 0;
  return (w + (2 * t)) * h;
};

/**
 * Usable roll width after margin AND cut-thickness loss. The cut-thickness
 * term scales with how many cuts fit across the roll (roll_width / width),
 * matching the reference `/quotes/strip` module's formula — confirmed
 * against a real-world example where omitting cut_thickness produced a
 * wrong width_adjustment (148 instead of the correct 147).
 */
export const calculateWidthAdjustment = (rollWidth, width, cutThickness, cutMargin) => {
  const roll = parseFloat(rollWidth) || 0;
  const w = parseFloat(width) || 0;
  const thickness = parseFloat(cutThickness) || 0;
  const margin = parseFloat(cutMargin) || 0;
  if (w <= 0) return roll - margin;
  return roll - Math.floor(((roll / w) * thickness) + margin);
};

export const calculatePiecesPerStrip = (widthAdjustment, width) => {
  const w = parseFloat(width) || 0;
  if (w <= 0) return 0;
  return Math.floor((parseFloat(widthAdjustment) || 0) / w);
};

/**
 * Usable roll width if the piece were rotated to use Height as the
 * strip-facing dimension instead of Width. Structurally different from
 * calculateWidthAdjustment: the cut-thickness term is floored on its own
 * (piece count across the roll), then multiplied by thickness, with the
 * margin subtracted separately — not floored together as one sum. This
 * means width === height does NOT generally produce equal adjustments.
 */
export const calculateHeightAdjustment = (rollWidth, height, cutThickness, cutMargin) => {
  const roll = parseFloat(rollWidth) || 0;
  const h = parseFloat(height) || 0;
  const thickness = parseFloat(cutThickness) || 0;
  const margin = parseFloat(cutMargin) || 0;
  if (h <= 0) return roll - margin;
  return roll - (Math.floor(roll / h) * thickness) - margin;
};

/**
 * % of the roll lost to margin + cut-thickness for a given orientation's
 * adjustment (widthAdjustment or heightAdjustment) — not the leftover from
 * fitting a whole number of pieces, which piecesPerStrip already accounts
 * for separately.
 */
export const calculateWastePercentageFromAdjustment = (adjustment, rollWidth) => {
  const roll = parseFloat(rollWidth) || 0;
  if (roll <= 0) return 0;
  const adj = parseFloat(adjustment) || 0;
  return (1 - (adj / roll)) * 100;
};

export const calculateStripsQuantity = (pieces, piecesPerStrip) => {
  const p = parseFloat(pieces) || 0;
  if (piecesPerStrip <= 0) return 1;
  const ratio = p / piecesPerStrip;
  return ratio < 1 ? 1 : Math.ceil(ratio);
};

export const calculateCutsQuantity = (stripsQuantity, piecesPerStrip) => {
  return stripsQuantity + piecesPerStrip;
};

/**
 * Material area actually consumed per strip: the FULL roll width (not just
 * the piece's own width) times the piece's other dimension — a strip cut
 * across the roll consumes the whole roll width regardless of how much of
 * it the piece uses. This is the cost basis for strip_price, distinct from
 * the smaller per-piece `cm2` shown for reference.
 */
export const calculateStripArea = (rollWidth, height) => {
  const roll = parseFloat(rollWidth) || 0;
  const h = parseFloat(height) || 0;
  return roll * h;
};

export const calculateStripPrice = (stripArea, productPrice, chargeCuts, cutsQuantity, pricePerCut) => {
  const price = parseFloat(productPrice) || 0;
  const base = (stripArea / 10000) * price;
  const cutsCost = chargeCuts ? cutsQuantity * (parseFloat(pricePerCut) || 0) : 0;
  return base + cutsCost;
};

export const calculatePricePerPiece = (stripsQuantity, stripPrice, pieces) => {
  const p = parseFloat(pieces) || 0;
  if (p <= 0) return 0;
  return stripsQuantity === 1 ? (stripPrice / p) : ((stripsQuantity * stripPrice) / p);
};

export const calculateStrip = ({
  rollWidth, cutMargin, cutThickness, width, height, pieces, productPrice, chargeCuts, pricePerCut
}) => {
  const cm2 = calculateCm2(width, height, cutThickness);
  const widthAdjustment = calculateWidthAdjustment(rollWidth, width, cutThickness, cutMargin);
  const heightAdjustment = calculateHeightAdjustment(rollWidth, height, cutThickness, cutMargin);
  const piecesPerStrip = calculatePiecesPerStrip(widthAdjustment, width);
  const wastePercentage = calculateWastePercentageFromAdjustment(widthAdjustment, rollWidth);
  const wastePercentageByHeight = calculateWastePercentageFromAdjustment(heightAdjustment, rollWidth);
  const betterByHeight = wastePercentageByHeight < wastePercentage;
  const stripsQuantity = calculateStripsQuantity(pieces, piecesPerStrip);
  const cutsQuantity = calculateCutsQuantity(stripsQuantity, piecesPerStrip);
  const stripArea = calculateStripArea(rollWidth, height);
  const stripPrice = calculateStripPrice(stripArea, productPrice, chargeCuts, cutsQuantity, pricePerCut);
  const pricePerPiece = calculatePricePerPiece(stripsQuantity, stripPrice, pieces);

  return {
    cm2,
    widthAdjustment,
    heightAdjustment,
    piecesPerStrip,
    wastePercentage,
    wastePercentageByHeight,
    betterByHeight,
    stripsQuantity,
    cutsQuantity,
    stripArea,
    stripPrice,
    pricePerPiece
  };
};
